import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

export function KioskFormDialog({ form, accentColor = "#2A2626", onClose }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const setVal = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/form/${form.form_id}/submit`, { data: values });
      setDone(data.success_message || "Thank you!");
      setTimeout(() => onClose(), 4000);
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please check the form and try again.");
      setSubmitting(false);
    }
  };

  const renderField = (f) => {
    const v = values[f.id];
    switch (f.type) {
      case "long_text":
        return <Textarea rows={4} placeholder={f.placeholder} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
      case "dropdown":
        return (
          <Select value={v || ""} onValueChange={(x) => setVal(f.id, x)}>
            <SelectTrigger className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
            <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o} className="text-lg">{o}</SelectItem>)}</SelectContent>
          </Select>
        );
      case "checkbox":
        return (
          <div className="space-y-2" data-testid={`kiosk-form-field-${f.id}`}>
            {(f.options || []).map((o) => {
              const arr = Array.isArray(v) ? v : [];
              const checked = arr.includes(o);
              return (
                <label key={o} className="flex items-center gap-3 text-lg cursor-pointer">
                  <Checkbox checked={checked} onCheckedChange={(c) => setVal(f.id, c ? [...arr, o] : arr.filter((x) => x !== o))} /> {o}
                </label>
              );
            })}
          </div>
        );
      case "single_checkbox":
        return (
          <label className="flex items-center gap-3 text-lg cursor-pointer" data-testid={`kiosk-form-field-${f.id}`}>
            <Checkbox checked={!!v} onCheckedChange={(c) => setVal(f.id, !!c)} /> {f.placeholder || f.label}
          </label>
        );
      case "date":
        return <Input type="date" value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
      case "number":
        return <Input type="number" inputMode="numeric" placeholder={f.placeholder} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
      case "email":
        return <Input type="email" placeholder={f.placeholder} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
      case "phone":
        return <Input type="tel" inputMode="tel" placeholder={f.placeholder} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
      default:
        return <Input placeholder={f.placeholder} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-12 text-lg" data-testid={`kiosk-form-field-${f.id}`} />;
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kiosk-form-dialog">
        {done ? (
          <div className="py-12 flex flex-col items-center text-center gap-4" data-testid="kiosk-form-success">
            <CheckCircle2 className="w-16 h-16" style={{ color: accentColor }} />
            <p className="text-2xl font-semibold text-[#2A2626]">{done}</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">{form.title || form.name}</DialogTitle>
              <DialogDescription className="sr-only">Please fill out this form.</DialogDescription>
            </DialogHeader>
            {form.description && <p className="text-[#5C5856]">{form.description}</p>}
            <div className="space-y-5 py-2">
              {(form.fields || []).map((f) => (
                <div key={f.id} className="space-y-1.5">
                  {f.type !== "single_checkbox" && (
                    <Label className="text-base font-medium">{f.label}{f.required && <span className="text-[#2A2626]"> *</span>}</Label>
                  )}
                  {f.description && <p className="text-sm text-[#5C5856]">{f.description}</p>}
                  {renderField(f)}
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-red-600" data-testid="kiosk-form-error">{error}</p>}
            <Button onClick={submit} disabled={submitting} className="w-full h-12 text-lg rounded-full text-white" style={{ backgroundColor: accentColor }} data-testid="kiosk-form-submit">
              {submitting ? "Submitting…" : (form.submit_text || "Submit")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
